import { useDispatch } from "react-redux";
import {
  fetchPortfolioContext,
  selectActivePortfolioApiId,
  selectPortfolioToolsReady,
  setParent,
} from "../../redux/slices/portfolioSlice";
import { store } from "../../redux/store";
import {
  REALTIME_PORTFOLIO_BOUNDARY_EN,
  resetConversationForWorkingPortfolioChange,
  resetViewsForWorkingPortfolioChange,
} from "../../util/portfolioSessionReset";
import {
  PARENT_PORTFOLIO_KEYS,
  getParentMeta,
  isValidParentKey,
  isValidPortfolioId,
  resolveParentKeyFromSelectToolArgs,
  resolvePortfolioIdFromSelectToolArgs,
} from "../../config/portfolioConfig";
import {
  REPORT_CHART_CATALOG_TO_KEY,
  resolveBusinessCycleWindow,
  SHARMA_TABLE_CATALOG_KEYS,
} from "../../config/sharmaReportsConfig";
import {
  fetchCurrentBusinessCycle,
  selectCurrentBusinessCycle,
} from "../../redux/slices/businessCycleSlice";
import IaListening from "../HomeSharma/index";
import ContentPage from "../ContentPageSharma/index";
import { useEffect, useRef, useState } from "react";
import { setListening, setSpeaking, setIdle } from "../../redux/slices/aiSlice";
import { setConversationLocale } from "../../redux/slices/uiSlice";
import { fetchLatestNews } from "../../services/newsApi";
import {
  contentTypeEnum,
  METHOD_NAMES,
  SIMULATION_MAP,
} from "../../util/enumsSharma";
import {
  addMessageToHistory,
  similarity,
  trimHistoryToLimit,
} from "../../util/variousMethods";
import { cloneTableWithPortfolioContext } from "../../util/portfolioTableContext";
import { getEphemeralKey, toolRunSimulation } from "../../util/iaUtilMethodsSharma";
import {
  formatSharmaChartIntegrationError,
  formatSharmaChartFailure,
  formatSharmaChartLoadError,
  formatSharmaTableFailure,
  formatSharmaTableLoadError,
} from "../../services/sharmaReportsApi";
import {
  detectSpanishLocaleRequest,
  formatSharmaPartialSuccess,
  resolveActiveLocale,
  REASON_CODES,
} from "../../services/sharmaReportMessages";
import {
  loadSharmaBusinessCycleScenario,
  loadSharmaChartByCatalogKey,
  loadSharmaTableByCatalogKey,
} from "../../services/sharmaReportHandlers";
import {
  matchMainCompositionChart,
  openMainPortfolioCompositionChart,
  summarizeMainAllocationForVoice,
} from "../../services/mainPortfolioComposition";
import LoadingOverlay from "../../components/LoadingOverlay";
import { HistoricalScenarios } from "../../services/riskFactor/riskData";
import { runHistoricalScenario } from "../../services/riskFactor/riskFunction";

const VOICE_MSG_SELECT_PORTFOLIO_FIRST =
  "You need to select a portfolio first.";
const VOICE_MSG_MAIN_READY =
  "Portfolio ready. If you wish, you may choose a sub-portfolio.";
const VOICE_MSG_INVALID_SUB =
  "I could not identify that sub-portfolio. Please try again.";
const VOICE_INSTR_SELECT_PORTFOLIO_FIRST =
  "Say only this one brief sentence in the active language: you need to select a portfolio first. Never name Main, principal, alternativo, or any product. Do not describe tool output. If the user's words clearly map to main-portfolio, call selectParentPortfolio; if they ask for portafolio alternativo or portfolio alternative, say that portfolio is not available; otherwise repeat only the generic sentence once.";
const VOICE_INSTR_MAIN_READY =
  "Say only this one brief sentence in the active language. EN: Portfolio ready. If you wish, you may choose a sub-portfolio. ES: Portafolio listo. Si deseas, puedes escoger un subportafolio. Do not list sub-portfolio names. Do not require a sub-portfolio before charts, tables, or simulations.";

const MC_VAR_TABLE_KEYS = new Set([
  "Monte Carlo Value at Risk",
  "Monte Carlo VaR",
  "Valor en Riesgo Monte Carlo",
  "VaR Monte Carlo",
]);

// AutoRyse's `confidence` field isn't guaranteed to be a 0-1 fraction vs. an
// already-scaled percentage, so this normalizes either convention for display.
function formatConfidencePercentSuffix(confidence) {
  if (typeof confidence !== "number" || Number.isNaN(confidence)) return "";
  const pct = confidence <= 1 ? confidence * 100 : confidence;
  return ` (${Math.round(pct)}% confidence)`;
}

async function connectWithEphemeralKey(offer) {
  const ephemeralKey = await getEphemeralKey();
  if (!ephemeralKey) throw new Error("❌ No se obtuvo ephemeral key");

  const url = "https://api.openai.com/v1/realtime/calls?model=gpt-realtime";

  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${ephemeralKey}`,
      "Content-Type": "application/sdp",
    },
    body: offer.sdp,
  });

  if (!res.ok) {
    const errText = await res.text();
    console.error("❌ Error respuesta OpenAI:", errText);
    throw new Error(`OpenAI Realtime API error: ${errText}`);
  }

  const answerSdp = await res.text();
  return { sdp: answerSdp };
}

export default function IaDashboard() {
  const dispatch = useDispatch();
  const SIM_THRESHOLD = 0.6;
  const CHARACTER_LIMIT = 12000;
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const micStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [outputText, setOutputText] = useState("Nothing");
  const [micLevel, setMicLevel] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const latestNewsRef = useRef([]);
  const aiResponseRef = useRef("");
  const [conversationHistory, setConversationHistory] = useState([]);
  const [currentContentType, setCurrentContentType] = useState(
    contentTypeEnum.nothing
  );
  function safeSend(payload, maxRetries = 40, intervalMs = 100) {
    return new Promise((resolve, reject) => {
      let tries = 0;
      const attempt = () => {
        tries++;
        const ch = dcRef.current;
        if (!ch) {
          if (tries >= maxRetries) return reject(new Error("No data channel"));
          return setTimeout(attempt, intervalMs);
        }
        if (ch.readyState === "open") {
          try {
            ch.send(
              typeof payload === "string" ? payload : JSON.stringify(payload)
            );
            return resolve();
          } catch (err) {
            return reject(err);
          }
        } else {
          if (tries >= maxRetries)
            return reject(new Error("DataChannel not open"));
          setTimeout(attempt, intervalMs);
        }
      };
      attempt();
    });
  }

  const startMicrophoneAnalysis = (stream) => {
    try {
      if (
        typeof window === "undefined" ||
        (!window.AudioContext && !window.webkitAudioContext)
      ) {
        console.warn(
          "AudioContext not available, skipping microphone analysis"
        );
        return;
      }

      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();

      if (audioContext.state === "suspended") {
        audioContext
          .resume()
          .then(() => {
            console.log("AudioContext resumed for microphone analysis");
          })
          .catch((err) => {
            console.error("Failed to resume AudioContext:", err);
            return;
          });
      }

      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.3;
      microphone.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      analyzeMicrophoneLevel();
    } catch (error) {
      console.error("Error setting up audio analysis:", error);
      setMicLevel(0);
    }
  };

  const analyzeMicrophoneLevel = () => {
    if (!analyserRef.current) return;

    try {
      const analyser = analyserRef.current;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const checkLevel = () => {
        if (!analyserRef.current) return;

        try {
          analyser.getByteTimeDomainData(dataArray);
          let sum = 0;
          for (let i = 0; i < bufferLength; i++) {
            const sample = (dataArray[i] - 128) / 128;
            sum += sample * sample;
          }
          const rms = Math.sqrt(sum / bufferLength);

          const level = Math.min(100, Math.max(0, rms * 300));
          setMicLevel(level);

          if (analyserRef.current) {
            animationFrameRef.current = requestAnimationFrame(checkLevel);
          }
        } catch (error) {
          console.error("Error in microphone level analysis:", error);
          setMicLevel(0);
        }
      };

      checkLevel();
    } catch (error) {
      console.error("Error starting microphone level analysis:", error);
      setMicLevel(0);
    }
  };

  const stopMicrophoneAnalysis = () => {
    try {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      analyserRef.current = null;
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close().catch((err) => {
          console.warn("Error closing AudioContext:", err);
        });
        audioContextRef.current = null;
      }
      setMicLevel(0);
    } catch (error) {
      console.error("Error stopping microphone analysis:", error);
      setMicLevel(0);
    }
  };

  useEffect(() => {
    window.gptRealtimeControl = {
      stop: () => {
        if (micStreamRef.current) {
          micStreamRef.current.getAudioTracks().forEach((track) => {
            track.enabled = false;
          });
        }
        dispatch(setIdle());
      },
      continue: () => {
        console.log("🎤 Unmuting microphone...");
        if (micStreamRef.current) {
          micStreamRef.current.getAudioTracks().forEach((track) => {
            track.enabled = true;
          });
        }
        dispatch(setListening());
      },
      getMicLevel: () => {
        if (
          currentContentType === contentTypeEnum.nothing &&
          analyserRef.current
        ) {
          return micLevel;
        }
        return 0;
      },
    };

    return () => {
      if (window.gptRealtimeControl) {
        delete window.gptRealtimeControl;
      }
    };
  }, [dispatch, micLevel, currentContentType]);

  const startWebRTC = async () => {
    try {
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: "stun:stun.l.google.com:19302" },
          { urls: "stun:stun1.l.google.com:19302" },
        ],
      });
      pcRef.current = pc;

      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      micStreamRef.current = micStream;
      micStream.getTracks().forEach((track) => pc.addTrack(track, micStream));

      setTimeout(() => {
        if (currentContentType === contentTypeEnum.nothing) {
          startMicrophoneAnalysis(micStream);
        }
      }, 1000);

      let audioCtx = null;
      let analyser = null;
      let dataArray = null;
      let volumeLoopRunning = false;

      pc.ontrack = (event) => {
        const audioEl = document.createElement("audio");
        audioEl.srcObject = event.streams[0];
        audioEl.autoplay = true;
        document.body.appendChild(audioEl);

        if (!audioCtx) {
          audioCtx = new AudioContext();
          analyser = audioCtx.createAnalyser();
          analyser.fftSize = 256;
          dataArray = new Uint8Array(analyser.frequencyBinCount);

          if (!volumeLoopRunning) {
            volumeLoopRunning = true;
            checkVolume();
          }
        }

        const source = audioCtx.createMediaStreamSource(event.streams[0]);
        source.connect(analyser);
      };

      let quietFrames = 0;
      const QUIET_LIMIT = 12;

      function checkVolume() {
        analyser.getByteTimeDomainData(dataArray);

        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const val = dataArray[i] - 128;
          sum += val * val;
        }
        const rms = Math.sqrt(sum / dataArray.length);

        const threshold = 10;
        const speakingNow = rms > threshold;

        if (rms > 10) {
          quietFrames = 0;
          dispatch(setSpeaking());
        } else if (quietFrames < QUIET_LIMIT) {
          quietFrames += 1;
        } else {
          dispatch(setListening());
        }
        requestAnimationFrame(checkVolume);
      }

      const dataChannel = pc.createDataChannel("oai-events");
      dcRef.current = dataChannel;

      dataChannel.onopen = async () => {
        try {
          const savedHistory = localStorage.getItem("conversationHistory");
          if (savedHistory) {
            const parsed = JSON.parse(savedHistory);
            const trimmedHistory = trimHistoryToLimit(parsed, CHARACTER_LIMIT);
            setConversationHistory(trimmedHistory);
            for (const msg of trimmedHistory) {
              await safeSend({
                type: "conversation.item.create",
                item: {
                  role: "system",
                  type: "message",
                  content: [
                    {
                      type: "input_text",
                      text: `[Conversation history for context only, do not re-execute tools or functions] ${msg.role}: ${msg.content}`,
                    },
                  ],
                },
              });
            }
          }

          await safeSend({
            type: "response.create",
            conversation: "default",
            response: {
              conversation: "auto",
              instructions: "Start transcribing the user's audio",
            },
          });
        } catch (err) {
          console.error("❌ Error enviando en onopen:", err);
        }
      };

      dataChannel.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          if (
            data.type ===
            "conversation.item.input_audio_transcription.completed"
          ) {
            const transcript = data?.transcript;
            if (
              !transcript ||
              typeof transcript !== "string" ||
              transcript.trim() === ""
            ) {
              return;
            }

            const clean = transcript.trim().toLowerCase();

            if (detectSpanishLocaleRequest(transcript)) {
              dispatch(setConversationLocale("es"));
            }

            addMessageToHistory(
              setConversationHistory,
              "user",
              clean,
              CHARACTER_LIMIT
            );
          }
          if (data.type === "response.output_audio_transcript.delta") {
            dispatch(setSpeaking());
            aiResponseRef.current += data.delta;
          }
          if (data.type === "response.done") {
            const finalAiMsg = aiResponseRef.current.trim();
            if (finalAiMsg) {
              addMessageToHistory(
                setConversationHistory,
                "assistant",
                finalAiMsg,
                CHARACTER_LIMIT
              );
            }
            aiResponseRef.current = "";
          }
          if (data.type === "response.done" && data.response?.output) {
            const parentKeyAtResponseStart =
              store.getState().portfolio?.selectedParentKey ?? null;
            const hadMainParentBeforeResponse =
              isValidParentKey(parentKeyAtResponseStart) &&
              parentKeyAtResponseStart === PARENT_PORTFOLIO_KEYS.MAIN;
            const parentCalledInResponse = data.response.output.some(
              (outputItem) =>
                outputItem.type === "function_call" &&
                outputItem.name === "selectParentPortfolio"
            );

            for (const item of data.response.output) {
              if (item.type === "function_call") {
                const { name, arguments: args } = item;
                console.log("🔧 [APP-SHARMA]", window.location.href, "Tool called:", name, "args:", args);
                let result = null;
                let confirmationMessage = "";

                if (name === "selectParentPortfolio") {
                  let parsed = {};
                  try {
                    parsed = JSON.parse(args || "{}");
                  } catch {
                    parsed = {};
                  }
                  const parentKey = resolveParentKeyFromSelectToolArgs(parsed);
                  if (!isValidParentKey(parentKey)) {
                    sendToolResponse(
                      { error: "invalid_parent" },
                      `${VOICE_MSG_SELECT_PORTFOLIO_FIRST} Fix parentKey using only the enum—do not read product names aloud.`,
                      false,
                      false,
                      true
                    );
                    return;
                  }
                  resetViewsForWorkingPortfolioChange(
                    dispatch,
                    setCurrentContentType,
                    setOutputText,
                    contentTypeEnum.nothing
                  );
                  dispatch(setParent(parentKey));
                  resetConversationForWorkingPortfolioChange(
                    setConversationHistory,
                    () => {
                      aiResponseRef.current = "";
                    }
                  );
                  try {
                    await safeSend({
                      type: "conversation.item.create",
                      item: {
                        role: "system",
                        type: "message",
                        content: [
                          {
                            type: "input_text",
                            text: `[Portfolio session boundary] ${REALTIME_PORTFOLIO_BOUNDARY_EN}`,
                          },
                        ],
                      },
                    });
                  } catch (boundaryErr) {
                    console.warn(
                      "Realtime portfolio boundary message failed:",
                      boundaryErr
                    );
                  }
                  const parentMeta = getParentMeta(parentKey);
                  const opened = await openMainPortfolioCompositionChart({
                    dispatch,
                    getState: () => store.getState(),
                    setCurrentContentType,
                    setOutputText,
                  });
                  const compositionVoice = opened.ok
                    ? `${opened.confirmationMessage} `
                    : `${opened.confirmationMessage} `;
                  sendToolResponse(
                    {
                      parentKey,
                      parent: parentMeta,
                      mainAllocation: opened.ok
                        ? summarizeMainAllocationForVoice(opened.payload)
                        : null,
                    },
                    `${compositionVoice}${VOICE_MSG_MAIN_READY} ${VOICE_INSTR_MAIN_READY}`,
                    false,
                    false,
                    true
                  );
                  return;
                }

                if (name === "selectSubPortfolio") {
                  const parentKey =
                    store.getState().portfolio?.selectedParentKey ?? null;
                  if (!isValidParentKey(parentKey)) {
                    sendToolResponse(
                      { error: "parent_not_selected" },
                      `${VOICE_MSG_SELECT_PORTFOLIO_FIRST} ${VOICE_INSTR_SELECT_PORTFOLIO_FIRST} Do not call selectSubPortfolio.`,
                      false,
                      false,
                      true
                    );
                    return;
                  }
                  if (parentKey !== PARENT_PORTFOLIO_KEYS.MAIN) {
                    sendToolResponse(
                      { error: "sub_requires_parent_change" },
                      `${VOICE_MSG_SELECT_PORTFOLIO_FIRST} ${VOICE_INSTR_SELECT_PORTFOLIO_FIRST} Do not call selectSubPortfolio.`,
                      false,
                      false,
                      true
                    );
                    return;
                  }
                  if (!hadMainParentBeforeResponse || parentCalledInResponse) {
                    sendToolResponse(
                      { error: "parent_not_selected" },
                      `${VOICE_MSG_SELECT_PORTFOLIO_FIRST} ${VOICE_INSTR_SELECT_PORTFOLIO_FIRST} Do not call selectSubPortfolio.`,
                      false,
                      false,
                      true
                    );
                    return;
                  }
                  let parsed = {};
                  try {
                    parsed = JSON.parse(args || "{}");
                  } catch {
                    parsed = {};
                  }
                  const portfolioId = resolvePortfolioIdFromSelectToolArgs(parsed);
                  if (!isValidPortfolioId(portfolioId)) {
                    sendToolResponse(
                      { error: "invalid_sub_portfolio" },
                      `${VOICE_MSG_INVALID_SUB} Fix portfolioKey using only the enum—do not read product names aloud.`,
                      false,
                      false,
                      true
                    );
                    return;
                  }
                  resetViewsForWorkingPortfolioChange(
                    dispatch,
                    setCurrentContentType,
                    setOutputText,
                    contentTypeEnum.nothing
                  );
                  try {
                    const data = await dispatch(
                      fetchPortfolioContext(portfolioId)
                    ).unwrap();
                    resetConversationForWorkingPortfolioChange(
                      setConversationHistory,
                      () => {
                        aiResponseRef.current = "";
                      }
                    );
                    try {
                      await safeSend({
                        type: "conversation.item.create",
                        item: {
                          role: "system",
                          type: "message",
                          content: [
                            {
                              type: "input_text",
                              text: `[Portfolio session boundary] ${REALTIME_PORTFOLIO_BOUNDARY_EN}`,
                            },
                          ],
                        },
                      });
                    } catch (boundaryErr) {
                      console.warn(
                        "Realtime portfolio boundary message failed:",
                        boundaryErr
                      );
                    }
                    const opened = await openMainPortfolioCompositionChart({
                      dispatch,
                      getState: () => store.getState(),
                      setCurrentContentType,
                      setOutputText,
                    });
                    const compositionVoice = opened.ok
                      ? `${opened.confirmationMessage} `
                      : `${opened.confirmationMessage} `;
                    sendToolResponse(
                      {
                        ...data,
                        mainAllocation: opened.ok
                          ? summarizeMainAllocationForVoice(opened.payload)
                          : null,
                      },
                      `${compositionVoice}Sub-portfolio is ready. You can now show charts or tables.`,
                      false,
                      false,
                      true
                    );
                  } catch {
                    sendToolResponse(
                      { error: "load_failed" },
                      "Could not load the sub-portfolio. Ask the user to try again.",
                      false,
                      false,
                      true
                    );
                  }
                  return;
                }

                if (name === "showChart") {
                  let graphName = "";
                  try {
                    graphName = JSON.parse(args || "{}")?.name ?? "";
                  } catch {
                    graphName = "";
                  }

                  const mainChartMatch = matchMainCompositionChart(
                    graphName,
                    SIM_THRESHOLD
                  );
                  if (mainChartMatch.matched) {
                    const opened = await openMainPortfolioCompositionChart({
                      dispatch,
                      getState: () => store.getState(),
                      setCurrentContentType,
                      setOutputText,
                    });
                    confirmationMessage = opened.confirmationMessage;
                    result = opened.ok
                      ? {
                          kind: "main_allocation_pie",
                          title: mainChartMatch.key,
                          mainAllocation: summarizeMainAllocationForVoice(
                            opened.payload
                          ),
                        }
                      : { error: "main_composition_unavailable" };
                    sendToolResponse(
                      result,
                      confirmationMessage,
                      false,
                      false,
                      true
                    );
                    return;
                  }
                }

                if (name === "getCurrentBusinessCycle") {
                  let bc = selectCurrentBusinessCycle(store.getState());
                  if (bc?.status !== "succeeded") {
                    try {
                      bc = await dispatch(fetchCurrentBusinessCycle()).unwrap();
                    } catch {
                      bc = null;
                    }
                  }
                  if (!bc || !bc.finalScenario) {
                    sendToolResponse(
                      { error: "unavailable" },
                      "The current market business cycle scenario is not available right now.",
                      false,
                      false,
                      true
                    );
                    return;
                  }
                  const confidenceSuffix = formatConfidencePercentSuffix(
                    bc.confidence
                  );
                  const secondaryBlurb = bc.secondaryScenario
                    ? `Secondary scenario: ${bc.secondaryScenario}. ${
                        bc.secondaryScenarioConditions || ""
                      }`.trim()
                    : "";
                  const resultToIa = {
                    finalScenario: bc.finalScenario,
                    confidence: bc.confidence,
                    consensusSummary: bc.consensusSummary,
                    secondaryScenario: bc.secondaryScenario,
                    secondaryScenarioConditions: bc.secondaryScenarioConditions,
                    agentsConsidered: bc.agentsConsidered,
                    timestampUtc: bc.timestampUtc,
                    curatedNews: (bc.curatedNews || []).map((n) => ({
                      title: n.title,
                      source: n.source,
                      date: n.date,
                      selectionReason: n.selectionReason,
                    })),
                  };
                  confirmationMessage = `Currently the market is in a ${bc.finalScenario} cycle${confidenceSuffix}.`;
                  setCurrentContentType(contentTypeEnum.text);
                  setOutputText({
                    name: `Current Business Cycle: ${bc.finalScenario}${confidenceSuffix}`,
                    description: [bc.consensusSummary, secondaryBlurb]
                      .filter(Boolean)
                      .join("\n\n"),
                  });
                  sendToolResponse(
                    resultToIa,
                    confirmationMessage,
                    false,
                    false,
                    true
                  );
                  return;
                }

                if (
                  name !== "showChart" &&
                  name !== "getCurrentBusinessCycle" &&
                  !selectPortfolioToolsReady(store.getState())
                ) {
                  sendToolResponse(
                    { error: "parent_not_selected" },
                    `${VOICE_MSG_SELECT_PORTFOLIO_FIRST} ${VOICE_INSTR_SELECT_PORTFOLIO_FIRST}`,
                    false,
                    false,
                    true
                  );
                  return;
                }

                if (name === "showChart") {
                  const { name: graphName } = JSON.parse(args || "{}");

                  let reportChartMatch = { key: null, score: 0 };
                  for (const key of Object.keys(REPORT_CHART_CATALOG_TO_KEY)) {
                    const score = similarity(graphName, key);
                    if (score > reportChartMatch.score) {
                      reportChartMatch = { key, score };
                    }
                  }

                  if (reportChartMatch.score >= SIM_THRESHOLD) {
                    setIsLoading(true);
                    try {
                      const portfolioApiId = selectActivePortfolioApiId(
                        store.getState()
                      );
                      if (!portfolioApiId) {
                        const loadErr = formatSharmaChartFailure(
                          resolveActiveLocale(),
                          REASON_CODES.INVALID_PORTFOLIO
                        );
                        setCurrentContentType(
                          contentTypeEnum.sharmaReportChart
                        );
                        setOutputText({
                          loadError: loadErr,
                          title: reportChartMatch.key,
                          reasonCode: REASON_CODES.INVALID_PORTFOLIO,
                        });
                        confirmationMessage = loadErr;
                        sendToolResponse(
                          {
                            error: "invalid_portfolio",
                            reason: REASON_CODES.INVALID_PORTFOLIO,
                          },
                          confirmationMessage
                        );
                        return;
                      }
                      const chartPayload = await loadSharmaChartByCatalogKey(
                        reportChartMatch.key,
                        portfolioApiId
                      );
                      if (!chartPayload) {
                        const loadErr = formatSharmaChartIntegrationError();
                        setCurrentContentType(
                          contentTypeEnum.sharmaReportChart
                        );
                        setOutputText({
                          loadError: loadErr,
                          title: reportChartMatch.key,
                          reasonCode: REASON_CODES.INTEGRATION_ERROR,
                        });
                        confirmationMessage = loadErr;
                        sendToolResponse(
                          {
                            error: "report_chart_empty",
                            reason: REASON_CODES.INTEGRATION_ERROR,
                          },
                          confirmationMessage
                        );
                        return;
                      }
                      setCurrentContentType(contentTypeEnum.sharmaReportChart);
                      setOutputText(chartPayload);
                      if (chartPayload.loadError) {
                        confirmationMessage = chartPayload.loadError;
                        sendToolResponse(
                          {
                            error: chartPayload.reasonCode || "report_chart_failed",
                            partial: false,
                            reason: chartPayload.reasonCode,
                          },
                          confirmationMessage
                        );
                        return;
                      }
                      if (chartPayload.partial) {
                        confirmationMessage =
                          chartPayload.partialNotice ||
                          formatSharmaPartialSuccess(
                            resolveActiveLocale(),
                            "chart",
                            reportChartMatch.key
                          );
                        sendToolResponse(
                          {
                            partial: true,
                            title: reportChartMatch.key,
                            kind: "sharma_report_chart",
                          },
                          confirmationMessage
                        );
                        return;
                      }
                      confirmationMessage = `Here is ${reportChartMatch.key}.`;
                      sendToolResponse(
                        {
                          title: reportChartMatch.key,
                          kind: "sharma_report_chart",
                        },
                        confirmationMessage
                      );
                      return;
                    } catch (e) {
                      const loadErr = formatSharmaChartLoadError(e);
                      setCurrentContentType(contentTypeEnum.sharmaReportChart);
                      setOutputText({
                        loadError: loadErr,
                        title: reportChartMatch.key,
                      });
                      confirmationMessage = loadErr;
                      sendToolResponse(
                        { error: "report_chart_failed", partial: false },
                        confirmationMessage
                      );
                      return;
                    } finally {
                      setIsLoading(false);
                    }
                  }

                  setCurrentContentType(contentTypeEnum.nothing);
                  setOutputText("Nothing");
                  result = { error: "chart_not_found" };
                  confirmationMessage =
                    "I could not find the chart you requested.";
                }
                if (name === "showTable") {
                  const { name: tablaName } = JSON.parse(args || "{}");
                  let bestMatch = { key: null, score: 0 };

                  for (const key of SHARMA_TABLE_CATALOG_KEYS) {
                    const score = similarity(tablaName, key);
                    if (score > bestMatch.score) bestMatch = { key, score };
                  }

                  if (bestMatch.score >= SIM_THRESHOLD) {
                    const portfolioApiId = selectActivePortfolioApiId(
                      store.getState()
                    );
                    setIsLoading(true);
                    try {
                      setCurrentContentType(contentTypeEnum.table);
                      if (!portfolioApiId) {
                        const loadErr = formatSharmaTableFailure(
                          resolveActiveLocale(),
                          REASON_CODES.INVALID_PORTFOLIO
                        );
                        result = {
                          loadError: loadErr,
                          reasonCode: REASON_CODES.INVALID_PORTFOLIO,
                        };
                        setOutputText(result);
                        confirmationMessage = loadErr;
                      } else {
                        const apiTable = await loadSharmaTableByCatalogKey(
                          bestMatch.key,
                          portfolioApiId
                        );
                        if (apiTable == null) {
                          const loadErr = formatSharmaTableFailure(
                            resolveActiveLocale(),
                            REASON_CODES.INTEGRATION_ERROR
                          );
                          result = {
                            loadError: loadErr,
                            reasonCode: REASON_CODES.INTEGRATION_ERROR,
                          };
                          setOutputText(result);
                          confirmationMessage = loadErr;
                        } else {
                          result = cloneTableWithPortfolioContext(
                            apiTable,
                            null,
                            null
                          );
                          setOutputText(result);
                          if (result.loadError) {
                            confirmationMessage = result.loadError;
                          } else if (result.partial) {
                            confirmationMessage =
                              result.partialNotice ||
                              formatSharmaPartialSuccess(
                                resolveActiveLocale(),
                                "table",
                                bestMatch.key
                              );
                          } else {
                            confirmationMessage = `Here you have the table of "${bestMatch.key}".`;
                            if (
                              bestMatch.key === "YTD Performance by index" ||
                              bestMatch.key ===
                                "YTD Performance by Strategy by Index"
                            ) {
                              confirmationMessage = `Here you have the table of YTD Performance by index. Here is the comparison of the portfolio with the market `;
                            }
                            if (
                              bestMatch.key === "Value at Risk" ||
                              bestMatch.key === "Value at Risk Analysis"
                            ) {
                              confirmationMessage = `Here you have the Value at Risk table for the selected portfolio.`;
                            }
                            if (MC_VAR_TABLE_KEYS.has(bestMatch.key)) {
                              confirmationMessage = `Here you have the Monte Carlo Value at Risk table for the selected portfolio.`;
                            }
                          }
                        }
                      }
                    } catch (e) {
                      const loadErr = formatSharmaTableLoadError(e);
                      result = { loadError: loadErr };
                      setCurrentContentType(contentTypeEnum.table);
                      setOutputText(result);
                      confirmationMessage = loadErr;
                    } finally {
                      setIsLoading(false);
                    }
                  } else {
                    result = { error: "Table not found" };
                    setCurrentContentType(contentTypeEnum.nothing);
                    setOutputText("Nothing");
                    confirmationMessage = "I didn't find the requested table.";
                  }
                }

                // === verMetodo ===
                if (name === "showMethod") {
                  const result = METHOD_NAMES["Longitudinal Monte Carlo"];
                  setOutputText(result);
                  confirmationMessage = `Here is the method of "${result.name}" and his descritpion is "${result.description}".`;
                  setCurrentContentType(contentTypeEnum.text);
                  sendToolResponse(result, confirmationMessage);
                  return;
                }
                // === verNoticias ===
                if (name === "showNews") {
                  let news = [];
                  try {
                    news = await fetchLatestNews();
                  } catch {
                    news = [];
                  }
                  latestNewsRef.current = news;
                  const titles = news.map((n) => n.title);
                  const confirmationMessage =
                    titles.length > 0
                      ? "Here are the news articles. How can I help you?"
                      : "No news articles found.";

                  setOutputText(titles);
                  setCurrentContentType(contentTypeEnum.newsList);
                  sendToolResponse(news, confirmationMessage);
                  return;
                }

                if (name === "showNewsDetail") {
                  const { title: newsTitle } = JSON.parse(args || "{}");
                  const cachedNews = latestNewsRef.current;

                  let bestMatch = { key: null, score: 0 };
                  for (const item of cachedNews) {
                    const score = similarity(newsTitle, item.title);
                    if (score > bestMatch.score)
                      bestMatch = { key: item.title, score };
                  }

                  if (bestMatch.score >= SIM_THRESHOLD) {
                    const newsItem = cachedNews.find(
                      (n) => n.title === bestMatch.key
                    );

                    setCurrentContentType(contentTypeEnum.newsDetail);
                    setOutputText(newsItem);
                    confirmationMessage = `Here is the news titled: "${newsItem.title}".`;
                    result = newsItem;
                  } else {
                    setCurrentContentType(contentTypeEnum.nothing);
                    confirmationMessage =
                      "I did not find the news you requested.";
                    result = { error: "News article not found" };
                  }
                }

                if (name === "showListHistoricalRiskFactor") {
                  const scenarioTitles = Object.keys(HistoricalScenarios);
                  const confirmationMessage =
                    scenarioTitles.length > 0
                      ? "Here are the historical risk factor scenarios. How can I help you?"
                      : "No historical scenarios found.";
                  setOutputText(scenarioTitles);
                  setCurrentContentType(contentTypeEnum.riskHistoricalList);

                  setOutputText(scenarioTitles);
                  var resultToIa = {
                    title: "historical risk factor scenarios",
                    values: scenarioTitles,
                  };
                  sendToolResponse(
                    resultToIa,
                    confirmationMessage,
                    false,
                    true
                  );
                  return;
                }

                if (name === "showHistoricalRiskFactorDetail") {
                  const parsed = JSON.parse(args || "{}");
                  const scenarioTitle = parsed.title;

                  const scenarioTitles = Object.keys(HistoricalScenarios);
                  let bestMatch = { key: null, score: 0 };

                  for (const item of scenarioTitles) {
                    const score = similarity(scenarioTitle, item);
                    if (score > bestMatch.score) {
                      bestMatch = { key: item, score };
                    }
                  }

                  if (bestMatch.score >= SIM_THRESHOLD && bestMatch.key) {
                    const result = runHistoricalScenario(bestMatch.key);
                    setCurrentContentType(
                      contentTypeEnum.historicalRiskFactorDetail
                    );
                    setOutputText({
                      title: bestMatch.key,
                      data: result,
                    });

                    confirmationMessage = `Here is the historical risk factor scenario titled: "${bestMatch.key}".`;

                    var resultToIa = {
                      title: bestMatch.key,
                      values: result,
                    };
                    sendToolResponse(resultToIa, confirmationMessage);
                    return;
                  } else {
                    setCurrentContentType(contentTypeEnum.nothing);
                    confirmationMessage =
                      "I did not find the historical risk factor scenario you requested.";

                    result = { error: "Historical scenario not found" };
                    sendToolResponse(result, confirmationMessage);
                    return;
                  }
                }
                if (name === "showCustomRiskCalculation") {
                  window.gptRealtimeControl?.stop?.();
                  setCurrentContentType(contentTypeEnum.customRiskCalculation);
                  confirmationMessage = `Here is a form to calculate the risk factor.`;
                  sendToolResponse("", confirmationMessage, false, true);
                  return;
                }
                if (name === "showRiskFactorOptions") {
                  setCurrentContentType(contentTypeEnum.nothing);
                  confirmationMessage = `Do you want to view historical scenarios or calculate a custom scenario?`;
                  sendToolResponse("", confirmationMessage, false, true);
                  return;
                }

                if (name === "showTableByIndex") {
                  const idxKey = "YTD Performance by index";
                  const portfolioApiId = selectActivePortfolioApiId(
                    store.getState()
                  );
                  setIsLoading(true);
                  try {
                    setCurrentContentType(contentTypeEnum.table);
                    if (!portfolioApiId) {
                      const loadErr = formatSharmaTableFailure(
                        resolveActiveLocale(),
                        REASON_CODES.INVALID_PORTFOLIO
                      );
                      result = {
                        loadError: loadErr,
                        reasonCode: REASON_CODES.INVALID_PORTFOLIO,
                      };
                      setOutputText(result);
                      confirmationMessage = loadErr;
                    } else {
                      const apiTable = await loadSharmaTableByCatalogKey(
                        idxKey,
                        portfolioApiId
                      );
                      if (apiTable == null) {
                        const loadErr = formatSharmaTableFailure(
                          resolveActiveLocale(),
                          REASON_CODES.INTEGRATION_ERROR
                        );
                        result = {
                          loadError: loadErr,
                          reasonCode: REASON_CODES.INTEGRATION_ERROR,
                        };
                        setOutputText(result);
                        confirmationMessage = loadErr;
                      } else {
                        result = cloneTableWithPortfolioContext(
                          apiTable,
                          null,
                          null
                        );
                        setOutputText(result);
                        if (result.loadError) {
                          confirmationMessage = result.loadError;
                        } else if (result.partial) {
                          confirmationMessage =
                            result.partialNotice ||
                            formatSharmaPartialSuccess(
                              resolveActiveLocale(),
                              "table",
                              idxKey
                            );
                        } else {
                          confirmationMessage = `Here you have the table of YTD Performance by index. Here is the comparison of the portfolio with the market`;
                        }
                      }
                    }
                  } catch (e) {
                    const loadErr = formatSharmaTableLoadError(e);
                    result = { loadError: loadErr };
                    setCurrentContentType(contentTypeEnum.table);
                    setOutputText(result);
                    confirmationMessage = loadErr;
                  } finally {
                    setIsLoading(false);
                  }
                }

                if (name === "runEconomicCycleSimulation") {
                  const parsed = JSON.parse(args || "{}");
                  let window = resolveBusinessCycleWindow(parsed?.window);
                  let usedAutoDetectedWindow = false;
                  if (!window) {
                    // No window named by the user — fall back to the market scenario
                    // AutoRyse currently detects, instead of always asking.
                    const detectedCycle = selectCurrentBusinessCycle(
                      store.getState()
                    );
                    const detectedWindow =
                      detectedCycle?.status === "succeeded"
                        ? resolveBusinessCycleWindow(detectedCycle.finalScenario)
                        : null;
                    if (detectedWindow) {
                      window = detectedWindow;
                      usedAutoDetectedWindow = true;
                    }
                  }
                  if (!window) {
                    const locale = resolveActiveLocale();
                    const msg =
                      locale === "es"
                        ? "Elige un escenario de ciclo económico: Recession, Innovation, Stagflation u Over-Extension."
                        : "Choose a business cycle scenario: Recession, Innovation, Stagflation, or Over-Extension.";
                    sendToolResponse({ needsWindow: true }, msg);
                    return;
                  }
                  const portfolioApiId = selectActivePortfolioApiId(
                    store.getState()
                  );
                  setIsLoading(true);
                  try {
                    setCurrentContentType(contentTypeEnum.table);
                    if (!portfolioApiId) {
                      const loadErr = formatSharmaTableFailure(
                        resolveActiveLocale(),
                        REASON_CODES.INVALID_PORTFOLIO
                      );
                      result = {
                        loadError: loadErr,
                        reasonCode: REASON_CODES.INVALID_PORTFOLIO,
                      };
                      setOutputText(result);
                      confirmationMessage = loadErr;
                    } else {
                      const apiTable = await loadSharmaBusinessCycleScenario(
                        portfolioApiId,
                        window
                      );
                      result = cloneTableWithPortfolioContext(
                        apiTable,
                        null,
                        null
                      );
                      setOutputText(result);
                      if (result.loadError) {
                        confirmationMessage = result.loadError;
                      } else if (result.partial) {
                        confirmationMessage =
                          result.partialNotice ||
                          formatSharmaPartialSuccess(
                            resolveActiveLocale(),
                            "table",
                            "Business Cycle Scenario"
                          );
                      } else {
                        confirmationMessage = usedAutoDetectedWindow
                          ? `Using ${window}, the currently detected market scenario. Here you have the Business Cycle Scenario table for ${window}.`
                          : `Here you have the Business Cycle Scenario table for ${window}.`;
                      }
                    }
                  } catch (e) {
                    const loadErr = formatSharmaTableLoadError(e);
                    result = { loadError: loadErr };
                    setCurrentContentType(contentTypeEnum.table);
                    setOutputText(result);
                    confirmationMessage = loadErr;
                  } finally {
                    setIsLoading(false);
                  }
                  if (result) {
                    sendToolResponse(result, confirmationMessage);
                  }
                  return;
                }

                if (SIMULATION_MAP[name]) {
                  sendToolResponse(
                    null,
                    "Running the simulation, this may take a little while...",
                    true
                  );
                  setIsLoading(true);
                  try {
                    const { result, resultContent, confirmationMessage } =
                      await toolRunSimulation(SIMULATION_MAP[name]);

                    const hasSimulationResult = Boolean(result && resultContent);
                    setCurrentContentType(
                      hasSimulationResult
                        ? contentTypeEnum.simulationTable
                        : contentTypeEnum.nothing
                    );
                    setOutputText(hasSimulationResult ? resultContent : "Nothing");
                    sendToolResponse(result, confirmationMessage);
                  } catch (simulationError) {
                    console.error(
                      "❌ Unexpected simulation execution error:",
                      simulationError
                    );
                    setCurrentContentType(contentTypeEnum.nothing);
                    setOutputText("Nothing");
                    sendToolResponse(
                      { error: "simulation_failed" },
                      "There was a problem running the simulation. Please try again."
                    );
                  } finally {
                    setIsLoading(false);
                  }
                  return;
                }

                if (result) {
                  sendToolResponse(result, confirmationMessage);
                }
              }
            }
          }
        } catch (err) {
          console.error("❌ Error en datachannel/tool dispatch:", err);
        }
      };

      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);

      const response = await connectWithEphemeralKey(offer);

      const remoteDesc = new RTCSessionDescription({
        type: "answer",
        sdp: response.sdp,
      });
      await pc.setRemoteDescription(remoteDesc);
      dispatch(setListening());
    } catch (err) {
      console.error("❌ Error en WebRTC:", err);
    }
  };
  function sendToolResponse(
    result,
    confirmationMessage,
    isPreliminary = false,
    isSpecialCase = false,
    allowWithoutPortfolioReady = false
  ) {
    if (!dcRef.current || dcRef.current.readyState !== "open") {
      console.error("DataChannel not ready");
      return;
    }

    if (
      !allowWithoutPortfolioReady &&
      !selectPortfolioToolsReady(store.getState())
    ) {
      console.warn("❌ Portfolio not ready. Cancelando flujo...");

      dcRef.current.send(
        JSON.stringify({
          type: "response.cancel",
          response: "auto",
        })
      );

      const systemText = `No portfolio is selected yet. Do not open charts, tables, news, risk, simulations, or other UI tools. ${VOICE_INSTR_SELECT_PORTFOLIO_FIRST}`;

      const instructionText = `${VOICE_MSG_SELECT_PORTFOLIO_FIRST} Do not describe tool output.`;

      dcRef.current.send(
        JSON.stringify({
          type: "conversation.item.create",
          item: {
            type: "message",
            role: "system",
            content: [
              {
                type: "input_text",
                text: systemText,
              },
            ],
          },
        })
      );

      dcRef.current.send(
        JSON.stringify({
          type: "response.create",
          response: {
            conversation: "auto",
            instructions: instructionText,
          },
        })
      );

      return;
    }

    if (!isPreliminary) {
      dcRef.current.send(
        JSON.stringify({
          type: "conversation.item.create",
          item: {
            type: "message",
            role: "system",
            content: [
              {
                type: "input_text",
                text: `Tool output (context only, do not describe or read this):\n${JSON.stringify(
                  result
                )}`,
              },
            ],
          },
        })
      );
    }

    let finalInstruction =
      `IMPORTANT: You just received JSON tool output. Do NOT describe or summarize the data. ` +
      `Only respond with: "${confirmationMessage} How can I help you?". ` +
      `You must NOT continue the conversation unless the user asks.`;

    if (isSpecialCase) {
      finalInstruction =
        `IMPORTANT: You just received tool output. ` +
        `Do NOT describe, read to the user or summarize anything. Only answer with this: "${confirmationMessage}".`;
    }

    if (isPreliminary) {
      finalInstruction = confirmationMessage;
    }

    dcRef.current.send(
      JSON.stringify({
        type: "response.create",
        response: {
          conversation: "auto",
          instructions: finalInstruction,
        },
      })
    );
  }

  const stopWebRTC = () => {
    if (pcRef.current) pcRef.current.close();
    stopMicrophoneAnalysis();
    setOutputText("Nothing");
  };

  useEffect(() => {
    startWebRTC();
    // stopWebRTC();
    return () => stopWebRTC();
  }, []);

  useEffect(() => {
    // Market-wide, not portfolio-scoped — fetch once per session, independent of
    // portfolio selection, so it's ready by the time the user asks about it.
    const promise = dispatch(fetchCurrentBusinessCycle());
    return () => promise.abort();
  }, [dispatch]);

  useEffect(() => {
    if (currentContentType !== contentTypeEnum.nothing) {
      stopMicrophoneAnalysis();
    } else if (
      micStreamRef.current &&
      currentContentType === contentTypeEnum.nothing
    ) {
      setTimeout(() => {
        startMicrophoneAnalysis(micStreamRef.current);
      }, 500);
    }
  }, [currentContentType]);

  const onBackFn = () => {
    window.gptRealtimeControl?.continue?.();
    if (currentContentType == contentTypeEnum.newsDetail) {
      const titles = latestNewsRef.current.map((n) => n.title);

      dcRef.current.send(JSON.stringify({ type: "response.cancel" }));
      setOutputText(titles);
      setCurrentContentType(contentTypeEnum.newsList);
    } else {
      setCurrentContentType(contentTypeEnum.nothing);
      setOutputText("Nothing");
    }
  };

  return (
    <>
      <div
        style={{ height: "100%", minHeight: "100dvh", position: "relative" }}
      >
        {currentContentType != contentTypeEnum.nothing ? (
          <ContentPage
            chartData={outputText}
            contentType={currentContentType}
            onBack={onBackFn}
            sendToolResponse={sendToolResponse}
            setCurrentContentType={setCurrentContentType}
            setOutputText={setOutputText}
          />
        ) : (
          <IaListening />
        )}
      </div>
      <LoadingOverlay show={isLoading} message="Loading..." />
    </>
  );
}
